import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["dateDisplayHint", "format", "inputSize", "inputRef", "momentType", "onChange", "type", "value", "onOpenChange"];
import { useCallback, useMemo } from 'react';
import { MomentTypes } from 'customer-data-properties/date/MomentTypes';
import { DATE } from 'customer-data-objects/property/PropertyTypes';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import I18n from 'I18n';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import UIMicroDateInput from 'UIComponents/dates/UIMicroDateInput';
import { getFormatStringForDateOrDateTime } from 'customer-data-property-utils/utils/dateAndDateTimeFormatting';
import styled from 'styled-components';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { fromMoment, makeMoment, toMoment } from 'foundations-components/transitional/utils/SimpleDate';
import { useIsUngatedForDisplayOptimizedFormControl } from '../v2/hooks/useIsUngatedForDisplayOptimizedFormControl';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { MicroDateInput } from 'foundations-components/transitional/components/MicroDateInput';
import { jsx as _jsx } from "react/jsx-runtime";
function toTimestamp(momentType, type, simpleDate) {
  if (!simpleDate) {
    return null;
  }
  if (type === 'date') {
    return makeMoment('utc', simpleDate).valueOf();
  } else {
    let result;
    if (momentType === MomentTypes.PORTAL) {
      result = makeMoment('portalTz', simpleDate);
    } else {
      result = toMoment(simpleDate);
    }
    if (type === 'endOfDay') {
      result = result.endOf('day');
    }
    return result.valueOf();
  }
}
function createMomentFromType(momentType, type, valueProp) {
  let value;
  if (valueProp || valueProp === 0) {
    value = Number(valueProp);
    if (isNaN(value)) {
      return null;
    }
  } else {
    return null;
  }
  if (type === 'date') {
    return I18n.moment.utc(value);
  }
  switch (momentType) {
    case MomentTypes.PORTAL:
      return I18n.moment.portalTz(value);
    case MomentTypes.USER:
      return I18n.moment.userTz(value);
    case MomentTypes.MOMENT:
      return I18n.moment(value);
    default:
      return null;
  }
}
// Explicitly hide UIDateInput when it's showing a placeholder in a floating form.
// This replicates the behavior of the legacy UIDatePicker.
//
// UIDateInput does not hide itself when given an empty placeholder, it always shows
// a placeholder value. The UIFloatingFormControl label overlaps the placeholder unless
// we explicitly hide it. (UIFloatingFormControl's logic for detecting placeholders
// only looks at immediate children and isn't compatible with the PropertyInput wrapper.)
//
// See https://git.hubteam.com/HubSpot/UIComponents/issues/7506
// See https://hubspot.slack.com/archives/C1WG3L3J6/p1570721400010300
// See https://git.hubteam.com/HubSpot/UIComponents/blob/master/HubStyle/static-2.9390/stylesheets/hubstyle/_form-labels.sass/#L73
// eslint-disable-next-line hubspot-dev/no-private-classes
const StyledDateInputWrapper = styled.div.withConfig({
  displayName: "CRMPropertyDateInput__StyledDateInputWrapper",
  componentId: "qqaygf-0"
})([".private-form__label--floating-as-placeholder + &{opacity:0 !important;}"]);
const CRMPropertyDateInput = props => {
  const {
      dateDisplayHint,
      format: inputFormatOverride,
      inputSize = 'large',
      inputRef,
      momentType = MomentTypes.USER,
      onChange,
      type = 'default',
      value,
      onOpenChange
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const passThroughProps = getTransferrableProps(rest);
  const isUngatedForDisplayOptimizedFormControl = useIsUngatedForDisplayOptimizedFormControl();
  const dateValue = useMemo(() => {
    const moment = createMomentFromType(momentType, type, value);
    if (moment) {
      return fromMoment(moment);
    }
  }, [momentType, type, value]);
  const handleChange = useCallback(evt => {
    const timestamp = toTimestamp(momentType, type, evt.target.value // 'as unknown as SimpleDate | null' is required because UIDateInput types onChange as `React.ChangeEvent<HTMLInputElement>` but actually broadcasts a `SyntheticEventType<SimpleDate | null>`
    );
    if (typeof onChange === 'function') {
      onChange(syntheticEvent(timestamp));
    }
  }, [momentType, type, onChange]);
  const format = useMemo(() => {
    const baseFormat = inputFormatOverride || 'L';
    if (type === 'date') {
      return getFormatStringForDateOrDateTime(DATE, dateDisplayHint, baseFormat, `${value}`, true);
    }
    return baseFormat;
  }, [dateDisplayHint, inputFormatOverride, type, value]);
  const sharedProps = useMemo(() => Object.assign({}, passThroughProps, {
    format,
    inputRef,
    onChange: handleChange,
    // HACK: prevent ESC key presses from propagating up and cancelling changes
    // See https://git.hubteam.com/HubSpot/UIComponents/issues/9877
    onKeyDown: _stopPropagation,
    onKeyPress: _stopPropagation,
    onKeyUp: _stopPropagation,
    onOpenChange,
    value: dateValue
  }), [dateValue, format, inputRef, handleChange, onOpenChange, passThroughProps]);
  if (isUngatedForDisplayOptimizedFormControl) {
    if (inputSize === 'small') {
      return (
        /*#__PURE__*/
        // @ts-expect-error - DateInput expects specific prop types:
        // 1. calendarPage prop from foundations-components does not use HiddenBrand<T> type
        // 2. format expects format to be one of: 'L' | 'YYYY-MM-DD' | 'LL' | 'll' | undefined
        // but getFormatStringForDateOrDateTime() potentially returns a different format string
        _jsx(MicroDateInput, Object.assign({
          "data-test-id": "micro-date-input"
        }, sharedProps))
      );
    }

    // @ts-expect-error - DateInput expects specific prop types:
    // 1. calendarPage prop from foundations-components does not use HiddenBrand<T> type
    // 2. format expects format to be one of: 'L' | 'YYYY-MM-DD' | 'LL' | 'll' | undefined
    // but getFormatStringForDateOrDateTime() potentially returns a different format string
    return /*#__PURE__*/_jsx(DateInput, Object.assign({
      "data-test-id": "date-input"
    }, sharedProps));
  }
  return /*#__PURE__*/_jsx(StyledDateInputWrapper, {
    "data-test-id": "date-input-wrapper",
    children: inputSize === 'small' ?
    /*#__PURE__*/
    // @ts-expect-error UIMicroDateInput generated type is incorrect, as it does not omit React.AllHTMLAttributes<HTMLInputElement>['value'] from the union.
    _jsx(UIMicroDateInput, Object.assign({
      "data-test-id": "micro-date-input"
    }, sharedProps)) :
    /*#__PURE__*/
    // @ts-expect-error - UIDateInput has strict format type requirements:
    // It expects format to be one of: 'L' | 'YYYY-MM-DD' | 'LL' | 'll' | undefined
    // but getFormatStringForDateOrDateTime() potentially returns a different format string
    _jsx(UIDateInput, Object.assign({
      "data-test-id": "date-input"
    }, sharedProps))
  });
};
export default CRMPropertyDateInput;
function _stopPropagation(e) {
  e.stopPropagation();
}